import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface Photo {
  id: string;
  photo_url: string;
}

interface TaskReferencePhotoCarouselProps {
  photos: Photo[];
  taskText: string;
  onClose?: () => void;
}

// Horizontal scrollable thumbnail strip for reference photos
export const TaskReferencePhotoStrip = ({ 
  photos, 
  label 
}: { 
  photos: Photo[];
  label?: string;
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (photos.length === 0) return null;

  return (
    <>
      <div className="space-y-2">
        {label && <p className="text-xs text-muted-foreground font-medium">{label}</p>}
        {/* Horizontal scroll on mobile, wrap on larger screens */}
        <div className="flex gap-2 sm:gap-3 overflow-x-auto sm:overflow-x-visible sm:flex-wrap pb-2 -mx-1 px-1 scrollbar-thin snap-x snap-mandatory">
          {photos.map((photo, index) => (
            <button
              key={photo.id}
              type="button"
              onClick={() => setSelectedIndex(index)}
              className={cn(
                "shrink-0 snap-start rounded-lg overflow-hidden border-2 border-primary/20 hover:border-primary transition-colors",
                "w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20", // Larger touch targets on mobile
                "active:scale-95 transition-transform" // Touch feedback
              )}
            >
              <img
                src={photo.photo_url}
                alt={`Reference ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      </div>

      {/* Fullscreen Lightbox */}
      <Dialog open={selectedIndex !== null} onOpenChange={() => setSelectedIndex(null)}>
        <DialogContent className="max-w-[100vw] w-screen h-[100dvh] max-h-[100dvh] p-0 bg-black border-none rounded-none sm:rounded-lg sm:max-w-4xl sm:h-auto sm:max-h-[90vh]">
          {selectedIndex !== null && (
            <TaskReferencePhotoCarousel
              photos={photos}
              taskText=""
              initialIndex={selectedIndex}
              onClose={() => setSelectedIndex(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// Full-screen carousel with swipe support
const TaskReferencePhotoCarousel = ({ 
  photos, 
  taskText,
  initialIndex = 0,
  onClose 
}: TaskReferencePhotoCarouselProps & { initialIndex?: number }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  };

  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    (e.currentTarget as any)._touchStartX = touch.clientX;
    (e.currentTarget as any)._touchStartY = touch.clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touch = e.changedTouches[0];
    const startX = (e.currentTarget as any)._touchStartX;
    const startY = (e.currentTarget as any)._touchStartY;
    const diffX = touch.clientX - startX;
    const diffY = touch.clientY - startY;
    
    // Only swipe if horizontal movement is greater than vertical
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      if (diffX > 0) {
        goPrev();
      } else {
        goNext();
      }
    }
  };

  return (
    <div 
      className="relative w-full h-full min-h-[60vh] sm:min-h-[50vh] flex items-center justify-center touch-pan-y"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close button - larger touch target on mobile */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-12 h-12 sm:w-10 sm:h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center active:bg-white/30"
        aria-label="Close"
      >
        <X className="w-6 h-6 sm:w-5 sm:h-5 text-white" />
      </button>

      {/* Navigation arrows - hidden on mobile (use swipe), visible on desktop */}
      {photos.length > 1 && (
        <>
          <button
            onClick={goPrev}
            className="hidden sm:flex absolute left-2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={goNext}
            className="hidden sm:flex absolute right-2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </>
      )}

      {/* Image */}
      <img
        src={photos[currentIndex].photo_url}
        alt={`Photo ${currentIndex + 1}`}
        className="max-w-full max-h-[75vh] sm:max-h-[80vh] object-contain px-2"
        loading="lazy"
      />

      {/* Dots indicator - larger on mobile */}
      {photos.length > 1 && (
        <div className="absolute bottom-16 sm:bottom-4 left-1/2 -translate-x-1/2 flex gap-2 sm:gap-1.5 p-2 rounded-full bg-black/30">
          {photos.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={cn(
                "rounded-full transition-all",
                "w-3 h-3 sm:w-2 sm:h-2", // Larger dots on mobile
                index === currentIndex ? "bg-white scale-110" : "bg-white/40"
              )}
              aria-label={`Go to photo ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Counter */}
      <div className="absolute top-4 left-4 text-white/80 text-sm font-medium px-3 py-1 rounded-full bg-black/30">
        {currentIndex + 1} / {photos.length}
      </div>

      {/* Swipe hint on mobile */}
      {photos.length > 1 && (
        <div className="sm:hidden absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-xs">
          Swipe to navigate
        </div>
      )}
    </div>
  );
};

export default TaskReferencePhotoCarousel;
